import React, { useContext } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import classNames from 'classnames';

import '../css/BasicIframeModalLauncherIcon.less';

import iconChatHelpDefault from '../img/icon-chat-help.svg';

import { SatCoreRegister } from '../SatCoreRegistry';

import BasicIframeModalLauncherService from '../services/BasicIframeModalLauncherService';

/**
 * BasicIframeModalLauncherIcon - A floating icon trigger for BasicIframeModal
 *
 * @description
 * A floating icon component that opens BasicIframeModal with a specified URL.
 * By default floats in the bottom-right corner of the screen.
 * ---
 * Useful for help icons, external links, or quick access to external tools.
 *
 * @example
 * // Minimal example 1 - basic floating help icon with predefined url
 * <BasicIframeModalLauncherIcon
 *   url='https://www.example.com/help'
 *   img={iconChatHelp}
 *   headerTitle={t('helpCenter')}
 * />
 *
 * @example
 * // Minimal example 2 - basic floating help icon via iframeModalLauncherObj
 * <BasicIframeModalLauncherIcon
 *   iframeModalLauncherObj={iframeModalLauncherObj}
 *   headerTitle={t('floatingHelpModalHeaderTitle')}
 * />
 *
 * @example
 * // Maximum options example - fully customized
 * <BasicIframeModalLauncherIcon
 *   url='https://codesandbox.io/embed/your-demo'
 *   img={iconSettings}
 *   headerTitle={t('interactiveDemo')}
 *   initWidth={1400}
 *   initHeight={900}
 *   position='top-left'
 *   disabled={false}
 *   closeOnDimmerClick={false}
 *   closeOnEscape={false}
 *   iframeProps={{
 *     allow: 'accelerometer; camera; encrypted-media; geolocation; gyroscope; microphone',
 *     sandbox: 'allow-scripts allow-same-origin allow-forms',
 *     loading: 'lazy',
 *     referrerPolicy: 'no-referrer'
 *   }}
 *   onOpen={() => console.info('Modal opened')}
 *   onClose={() => console.info('Modal closed')}
 *   className='custom-launcher-premium'
 *   style={{ zIndex: 9500, transform: 'scale(1.2)' }}
 * />
 */
const BasicIframeModalLauncherIcon = observer((props = {}) => {
  const {
    className = '',
    closeOnDimmerClick = false,
    closeOnEscape = true,
    disabled = false,
    headerTitle = 'External Content',
    iframeModalLauncherObj = {},
    iframeProps,
    initHeight,
    initWidth,
    onClose = () => {},
    onOpen = () => {},
    position = 'bottom-right', // 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
    style = {},
    top
  } = props;

  const img = props.img || iconChatHelpDefault;

  const {
    dialogManager
  } = useContext(MobXProviderContext);

  const handleLaunchModal = async () => {
    if (disabled) {
      return;
    }

    await BasicIframeModalLauncherService.openIframeModalFromLauncher(
      dialogManager,
      iframeModalLauncherObj,
      {
        closeOnDimmerClick,
        closeOnEscape,
        headerTitle,
        iframeProps,
        initHeight,
        initWidth,
        onClose,
        onOpen,
        top,
        url: props?.url
      }
    );
  };

  const renderIcon = () => {
    return (
      <img
        alt={headerTitle}
        src={img}
        style={{ display: 'block' }}
      />
    );
  };

  return (
    <div
      className={classNames('BasicIframeModalLauncherIcon', className)}
      style={style}>
      <div
        aria-label={headerTitle}
        className={`basic-iframe-modal-launcher-icon-wrapper basic-iframe-modal-launcher-icon-${position}`}
        onClick={handleLaunchModal}
        onKeyDown={async (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            await handleLaunchModal();
          }
        }}
        role='button'
        tabIndex={0}>
        <div className='basic-iframe-modal-launcher-icon'>
          {renderIcon()}
        </div>
      </div>
    </div>
  );
});

export default BasicIframeModalLauncherIcon;

SatCoreRegister('BasicIframeModalLauncherIcon', BasicIframeModalLauncherIcon);
